﻿import {QuoteWizardStateService} from './../quoteWizardState.service';
import {QuoteWizardNavigationService} from './../quoteWizardNavigation.service';
import {IQuoteWizardNode} from './../quoteWizardNode';
import * as _ from 'lodash';
import {WizardFocus} from '../../core/services/wizardNavigationService/wizardFocus';
import {IWizardCurrentNodeChangedEventArgs, NavigationDirection} from '../../core/services/wizardNavigationService/wizardNavigationService';
import {LocationAggregate} from '../../models/models';

interface IDictionary<T> {
    [key: string]: T;
}

export class QuoteWizardNavigationController {
        
    public wizardFoci: WizardFocus[];
    public currentWizardFocus: WizardFocus;
    public currentSectionStateName: string;
    public menuSectionCollapsedState: IDictionary<boolean> = {};
    public isMenuNavigationEnabled: boolean;

    private get locationBumperStateName(): string {
        return 'shellFrame.quoteWizardFrame.wizard.locationBumper';
    }

    private get addAnotherLocationStateName(): string {
        return 'shellFrame.quoteWizardFrame.wizard.addAnotherLocation';
    }
    
    private get nodeTemplate(): IQuoteWizardNode[]{
        //shortcut so we dont have to type assert this all over.
        return this.navigationService.nodeTemplate as IQuoteWizardNode[];
    }

    private get locations(): LocationAggregate[]{
        //shortcut so we dont have to dot in so much everywhere.
        return this.stateService.viewModel.clientStateContext.submissionRequest.locations;
    }

    public static $inject = ['quoteWizardNavigationService', 'quoteWizardStateService', '$state', '$injector', '$scope'];

    constructor(
        private navigationService: QuoteWizardNavigationService,
        public stateService: QuoteWizardStateService,
        private $state: ng.ui.IStateService,
        private $injector: any,
        private $scope: angular.IScope
    ) {
        this.activate();
    }

    public activate(): void {
        if (this.stateService.viewModel.isReturningUserAfterWizardCompletion) {
            return;
        }

        this.isMenuNavigationEnabled = !(this.stateService.viewModel.finishedSubmissionResponse);
        const menuEnabledChangedListener = this.stateService.subscribeOnMenuNavigationEnabledChanged(this.onMenuNavigationEnabledChanged);
        this.$scope.$on("$destroy", menuEnabledChangedListener);

        this.wizardFoci = [];
        this.setInitialMenuSectionCollapsedState();
        this.getIncludedNodes().then(() => { this.expandCollapseOnNavigation(this.navigationService.currentNode); });
        
        const wizardNodeChangeListener = this.navigationService.subscribeOnWizardNodeChange(this.onWizardNodeChange);
        this.$scope.$on("$destroy", wizardNodeChangeListener);
        
    }

    private onMenuNavigationEnabledChanged = (event: ng.IAngularEvent, isEnabled: boolean) => {
        this.isMenuNavigationEnabled = isEnabled;
    }

    private onWizardNodeChange = (event: ng.IAngularEvent, args: IWizardCurrentNodeChangedEventArgs) => {
        if (args.direction === NavigationDirection.Restart) {
            this.restartWizard();
        } else {
            this.expandCollapseOnNavigation(args.currentNode);
        }

        //we might need to queue/chain this, or have a cancel mechanism?.  When calling this.navigationService.goBack(lastLocationStep) and 
        //this.navigationService.goNext() back to back, it ran into a slight race condition when I was calling expandCollapseOnNavigation
        // in the then.  But I don't think it needed to be in there anyways.
        this.getIncludedNodes();
    }

    private restartWizard() {
        this.currentSectionStateName = undefined;
        this.currentWizardFocus = undefined;
        this.setInitialMenuSectionCollapsedState();
    }

    private getIncludedNodes(): ng.IPromise<any> {
        // clear out the foci array and rebuild it
        this.wizardFoci.length = 0;
        return this.navigationService.forEachIncludedNode(this.onIncludeNode);
    }

    private onIncludeNode = (action: WizardFocus) => {
        this.wizardFoci.push(action);
    }

    private setInitialMenuSectionCollapsedState(): void {
        _.forEach(this.nodeTemplate, (node: IQuoteWizardNode) => {
            if (angular.isDefined(node.subjectData) && angular.isArray(node.subjectData)) {
                for (let i = 0; i < node.subjectData.length; i++) {
                    this.addMenuSectionState(node.stateName, i);
                }
            } else {
                this.addMenuSectionState(node.stateName, 0);
            }
        });
   }

    private getMenuSectionKey(stateName: string, index: number): string {
        return `${stateName}${index}`;
    }

    private addMenuSectionState(stateName: string, index: number, initialState: boolean = true): void {
        this.menuSectionCollapsedState[this.getMenuSectionKey(stateName, index)] = initialState;
    }

    //So this bit here is handling the automatic expansion and collapse of the sections as they step through the wizard
    private expandCollapseOnNavigation(currentFocus: WizardFocus): void {
        if (!currentFocus) {
            return;
        }
        if (angular.isUndefined(this.currentSectionStateName)) {
            this.currentWizardFocus = currentFocus;
            const parent = this.getCurrentNodeParent();
            this.currentSectionStateName = parent.stateName;
            let index = 0;
            if (parent.subjectData !== this.currentWizardFocus.subject) 
                index = parent.subjectData.indexOf(this.currentWizardFocus.subject);
            this.menuSectionCollapsedState[this.getMenuSectionKey(this.currentSectionStateName, index)] = false;
            return;
        }

        //get these for comparison to the new current node
        const previousWizardFocus = this.currentWizardFocus;
        const previousParent = this.getCurrentNodeParent();

        //now set to the current node
        this.currentWizardFocus = currentFocus;
        const currentParent = this.getCurrentNodeParent();
        this.currentSectionStateName = currentParent.stateName;

        let currentSubjectIndex: number = 0;
        if (this.currentSectionStateName === this.locationBumperStateName && currentFocus.node.stateName !== this.locationBumperStateName)
            currentSubjectIndex = currentParent.subjectData.indexOf(this.currentWizardFocus.subject);

        let previousSubjectIndex: number = 0;
        if (previousParent.stateName === this.locationBumperStateName && previousWizardFocus.node.stateName !== this.locationBumperStateName) {
            previousSubjectIndex = previousParent.subjectData.indexOf(previousWizardFocus.subject);
        }

        if (previousParent.stateName !== this.currentSectionStateName || (currentSubjectIndex !== previousSubjectIndex)) {
            //open the current section
            this.menuSectionCollapsedState[this.getMenuSectionKey(this.currentSectionStateName, currentSubjectIndex)] = false;
            //close the previous section
            this.menuSectionCollapsedState[this.getMenuSectionKey(previousParent.stateName, previousSubjectIndex)] = true;
        }
    }

    public getMenuLabel(node: IQuoteWizardNode): string {
        if (angular.isArray(node.menuLabel)) {
            return this.$injector.invoke(node.menuLabel);
        }
        else {
            return node.menuLabel as string;
        }
    }

    private getCurrentNodeParent(): IQuoteWizardNode {
        if (angular.isDefined(this.currentWizardFocus.node.parent) && this.currentWizardFocus.node.parent.stateName.length > 0) {
            return this.currentWizardFocus.node.parent as IQuoteWizardNode;
        }
        return this.currentWizardFocus.node as IQuoteWizardNode;
    }

    private getWizardFocusChildren(templateNode: IQuoteWizardNode, subject?: any): WizardFocus[] {
        const children: WizardFocus[] = [];

        _.forEach(templateNode.children, (node: IQuoteWizardNode) => {
            const child = this.getWizardFocusNode(node, subject);
            if (angular.isDefined(child))
                children.push(child);
        });

        return children;
    }

    private getWizardFocusNode(node: IQuoteWizardNode, subject?: any): WizardFocus {
        const focus = _.find(this.wizardFoci, (f: WizardFocus) => {
            return f.node.stateName === node.stateName && (angular.isUndefined(subject) || f.subject === subject);
        });

        return focus;
    }

    private isNodeComplete(node: IQuoteWizardNode, subject?: any): boolean {
        if (!this.isMenuNavigationEnabled)
            return false;

        const focus = this.getWizardFocusNode(node, subject);

        const historyNode = _.find(this.navigationService.getHistory(), (h: WizardFocus) => {
            return h.node.stateName === focus.node.stateName && h.subject === focus.subject;
        });

        return angular.isDefined(historyNode);
    }

    private isSectionComplete(sectionTemplate: IQuoteWizardNode, subject?: any): boolean {
        //get all of the child nodes that were included
        const children = this.getWizardFocusChildren(sectionTemplate, subject);

        if (children.length === 0){
            const historyNode = _.find(this.navigationService.getHistory(), (h: WizardFocus) => {
                return h.node.stateName === sectionTemplate.stateName;
            });
            return angular.isDefined(historyNode);
        }

        //now see if all of the included nodes are in the history
        const result = _.every(children, (child: WizardFocus) => {
            const historyNode = _.find(this.navigationService.getHistory(), (h: WizardFocus) => {
                return h.node.stateName === child.node.stateName && h.subject === child.subject;
            });
            return angular.isDefined(historyNode);
        });

        return result;
    }

    private navigateTo(node: IQuoteWizardNode, subject?: any): void {
        if (!this.isMenuNavigationEnabled)
            return;

        if (this.hideNav()) {
            this.stateService.broadcastMenuNavigationClick(true);
        }

        const focus = this.getWizardFocusNode(node, subject);

        const historyNode = _.find(this.navigationService.getHistory(), (h: WizardFocus) => {
            return h.node.stateName === focus.node.stateName && h.subject === focus.subject;
        });

        if (angular.isDefined(historyNode))
            this.navigationService.goBack(historyNode);

        //it is not in history so do nothing.
        return;
    }

    private hideNav() {
        return this.isMobileDevice() || this.isNarrowPage()
    }

    private isMobileDevice() {
        var check = false;
        (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor);
        return check;
    };

    private isNarrowPage() {
        return $(window).innerWidth() < 450;
    };
}
